import { UpdatedParamsAndRefs } from "./parameterTypes";
import { FusaReply } from "./replyTypes";

/**
 * What the code runner is handed, and what it answers with.
 *
 * Plain objects rather than the generated proto classes: these cross the bridge
 * to veriform-desktop, which turns them into a `RunRequest` and back. The field
 * names follow the proto's own so the two can be read side by side
 * (code_runner_pb.d.ts there). Only veriform-desktop knows the proto; only this
 * app knows what a parameter is.
 */

/** A parameter holding one value: `Param` on the wire. */
export interface CodeRunParam {
  identifier: string;
  /** Empty for an output -- the runner fills it in. */
  value: string;
  /** The custom field's type, which is what Python keys its coercion off. */
  type: string;
}

/** A parameter holding several: `ArrayParam`. */
export interface CodeRunArrayParam {
  identifier: string;
  type: string;
  values: string[];
  objectValues: CodeRunObjectParam[];
}

/**
 * A parameter whose value is an object: `ObjectParam`.
 *
 * Keyed by the object's property key, which is what the proto carries
 * (`getPropertiesMap`) and what the runner matches a returned object against.
 */
export interface CodeRunObjectParam {
  identifier: string;
  properties: Record<string, CodeRunParam>;
}

/**
 * Built by `POST /api/codes/:id/prepare` and relayed to the runner unchanged.
 *
 * Split three ways because the runner's proto is: a parameter with one plain
 * value, one with several, and one whose value is an object.
 */
export interface CodeRunRequest {
  source: string;
  inputParams: CodeRunParam[];
  inputArrayParams: CodeRunArrayParam[];
  inputObjectParams: CodeRunObjectParam[];
  outputParams: CodeRunParam[];
  outputArrayParams: CodeRunArrayParam[];
  outputObjectParams: CodeRunObjectParam[];
}

/**
 * A file a code output produced, still only on the machine that ran it.
 *
 * The field it belongs to is already made; its file is pending until
 * veriform-desktop uploads the bytes and confirms through
 * `POST /api/files/:id/confirm`.
 */
export interface CodePendingAsset {
  /** The `files` row, which the confirm takes. */
  id: string;
  /** Where the runner wrote it, local to that machine. */
  path: string;
  uploadUrl: string;
}

/** `RunReply`, as veriform-desktop relays it back. */
export interface CodeRunReply {
  stdout: string;
  stderr: string;
  returnCode: number;
  outputParams: CodeRunParam[];
  outputArrayParams: CodeRunArrayParam[];
  outputObjectParams: CodeRunObjectParam[];
}

/**
 * The states a run leaves behind in `returnCode`, mirroring desktop's
 * `CodeStatus` (models/code.ts there). Anything else is the process's own exit
 * code, where 0 is a clean run.
 */
export enum CodeStatus {
  RUN_PENDING = 100,
  FAILURE = 400,
}

export interface PrepareCodeRunReply extends FusaReply {
  code: CodeParams;
  runRequest: CodeRunRequest;
}

/** What applying a run changed, for the page to show without a reload. */
export interface CodeRunResultReply extends UpdatedParamsAndRefs, FusaReply {
  code: CodeParams;
  pendingAssets: CodePendingAsset[];
}

export interface CodeCreateParams {
  script?: string;
  operationId: string;
  lastRanAt?: Date;
  stdout?: string;
  stderr?: string;
  returnCode?: number;
}

export interface CodeParams extends CodeCreateParams {
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  /** The org of the code's operation. CodeAccess sets it on create from the writer org; callers never choose it. */
  propelOrgId?: string;
}

export interface CodeWrapper {
  code: CodeParams;
}

export interface CodeReply extends FusaReply {
  code: CodeParams;
}
